import { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { type StripePlan, STRIPE_PRODUCTS } from '@/lib/stripe-config';
import { cn } from '@/lib/utils';
import { trackSavingsCalculatorInteraction } from '@/lib/analytics';

// Every plan with its yearly cost and a select button. The Free card is the downgrade entry point
// (the page routes it to handleSelectFree, VOY-607), not the paid checkout path.

// Monthly subscription costs (per year) - Updated prices
const MONTHLY_COSTS: Record<StripePlan, number> = {
  VOY_FREE: 0,         // Free
  VOY_PRO: 60,         // $5/month * 12 = Pro
  VOY_BUSINESS: 84,    // $7/month * 12 = Business
  VOY_FAMILY: 36,      // $3/month * 12 = Family
};

// Annual subscription costs
const ANNUAL_COSTS: Record<StripePlan, number> = {
  VOY_FREE: 0,         // Free
  VOY_PRO: 48,         // $48/year - Pro Annual
  VOY_BUSINESS: 70,    // $70/year - Business Annual
  VOY_FAMILY: 30,      // $30/year - Family Annual
};

interface SavingsCalculatorProps {
  onSubscribe?: (plan: StripePlan) => void;
  isAnnual?: boolean;
}

export function SavingsCalculator({ onSubscribe, isAnnual = false }: SavingsCalculatorProps) {
  // Track view on mount
  useEffect(() => {
    trackSavingsCalculatorInteraction('view');
  }, []);

  const costs = isAnnual ? ANNUAL_COSTS : MONTHLY_COSTS;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <section className="bg-transparent py-12 md:py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4 text-white drop-shadow-lg">
            All plans
          </h2>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto drop-shadow-md">
            Choose a plan, or switch to Free.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {(Object.keys(STRIPE_PRODUCTS) as StripePlan[]).map((plan) => {
            const product = STRIPE_PRODUCTS[plan];

            return (
              <Card
                key={plan}
                data-testid={`savings-card-${plan}`}
                className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border-2 border-white/20 hover:border-white/30 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl p-6 md:p-8 transition-all duration-300"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                    {product.name}
                  </h3>
                  <div className="text-sm text-white/70">
                    {formatCurrency(costs[plan])}/year
                  </div>
                </div>

                {onSubscribe && (
                  <button
                    onClick={() => {
                      trackSavingsCalculatorInteraction('subscribe', undefined, plan);
                      onSubscribe(plan);
                    }}
                    className={cn(
                      'w-full py-3 px-4 rounded-full font-semibold transition-all mt-4',
                      'bg-white/10 hover:bg-white/20 text-white'
                    )}
                  >
                    Choose Plan
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
